import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Bot,
  FileText,
  AlertTriangle,
  CheckCircle,
  Eye,
  DollarSign,
  Shield,
  Scale,
  Cog,
  Building,
  Clock,
  Play,
  Loader2,
  RefreshCw,
  Target,
  Edit3,
  Save,
  X,
  Download,
} from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext"; // Added import for useAuth
import { aiReviewService, AIReviewResult } from "../services/aiReviewService";
import documentService, { Document } from "../services/documentService";
import { dealService } from "../services/dealService";
import { personaService } from "../services/personaService";
import { getPreviousStepNavigation } from "../utils/stepNavigation";
import ReactMarkdown from 'react-markdown';
import type { User } from "@/types/auth";

// Helper function to extract report content
const extractReportContent = (reportContent: any): string => {
  if (!reportContent) return 'No content available';

  if (typeof reportContent === 'string') {
    return reportContent;
  }

  if (reportContent.content) {
    if (typeof reportContent.content === 'string') {
      return reportContent.content;
    }

    // Try to find content in the object
    const contentKeys = ['content', 'text', 'markdown', 'body'];
    for (const key of contentKeys) {
      if (reportContent.content[key] && typeof reportContent.content[key] === 'string') {
        return reportContent.content[key];
      }
    }
  }

  // If all else fails, stringify the object
  return JSON.stringify(reportContent, null, 2);
};

interface PersonaAnalysis {
  id: string;
  name: string;
  icon: any;
  color: string;
  status: "completed" | "reviewing" | "pending";
  assignedTo: string;
  findings: {
    summary: string;
    risks: string[];
    recommendations: string[];
    confidence: number;
  };
  reviewedAt?: string;
  comments: {
    id: string;
    user: string;
    text: string;
    timestamp: string;
  }[];
}

interface ExtractedData {
  category: string;
  chapter: string;
  field: string;
  value: string;
  confidence: number | string; // Allow both number and "N/A"
  source_document: string;
  page_reference?: string;
  verification_status: "VERIFIED" | "FLAGGED" | "PENDING";
  persona: string;
  personaType: string;
  notes?: string;
}

const AIReview = () => {
  const [searchParams] = useSearchParams();
  const dealId = searchParams.get("dealId") || "1";
  const [dealTitle, setDealTitle] = useState<string>("");
  const { toast } = useToast();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth(); // Added useAuth hook

  const getUserId = (u?: User | null) => {
    if (!u) return null;
    if ("id" in u && typeof (u as any).id === "string") return (u as any).id;
    if ("userId" in u) return (u as any).userId;
    if ("sub" in u) return (u as any).sub;
    if ((u as any).uid) return (u as any).uid;
    return null;
  };
  const userId = getUserId(user);

  const [deal, setDeal] = useState<any | null>(null);

  const isAdmin = user?.role === "ADMIN";
  const isAnalyst = user?.role === "ANALYST";
  const isReviewerRole = user?.role === "REVIEWER";
  const isViewerRole = user?.role === "VIEWER";

  const includesUser = (arr?: any[]) =>
    Array.isArray(arr) &&
    !!userId &&
    arr.some((x) => x === userId || x?.id === userId || x?.userId === userId);

  const isAssignedAnalyst =
    isAnalyst &&
    !!userId &&
    (
      deal?.assignedAnalystId === userId ||
      deal?.assigned_analyst_id === userId ||
      includesUser(deal?.assignedAnalysts) ||
      includesUser(deal?.analysts) ||
      includesUser(deal?.assigned_users)
    );

  const isAssignedReviewerOrViewer =
    (!!userId &&
      (
        deal?.assignedReviewerId === userId ||
        deal?.assigned_reviewer_id === userId ||
        includesUser(deal?.assignedReviewers) ||
        includesUser(deal?.reviewers) ||
        includesUser(deal?.viewers)
      )) ||
    isReviewerRole ||
    isViewerRole;

  // Get navigation info for previous step
  const previousStepNav = getPreviousStepNavigation('ai-review', dealId);

  // New state for real AI review system
  const [isLoading, setIsLoading] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [reviewData, setReviewData] = useState<{
    results: AIReviewResult[];
    summary: {
      dealId: string;
      isRunning: boolean;
      overallProgress: number;
      totalDocuments: number;
      totalProcessedDocuments: number;
      totalPersonas: number;
      completedPersonas: number;
      failedPersonas: number;
      estimatedCompletionTime: Date | null;
    };
  } | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [authError, setAuthError] = useState<string | null>(null);

  // Deal-specific personas state
  const [dealPersonas, setDealPersonas] = useState<string[]>([]);
  const [availablePersonas, setAvailablePersonas] = useState<any[]>([]);

  // Legacy state for compatibility (will be removed once real data is fully integrated)
  const [personas, setPersonas] = useState<PersonaAnalysis[]>([]);

  const [extractedData, setExtractedData] = useState<ExtractedData[]>([]);

  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [newComment, setNewComment] = useState("");
  const [activePersona, setActivePersona] = useState<string | null>(null);

  // New state for editing Q&A data
  const [isEditModeEnabled, setIsEditModeEnabled] = useState<Record<string, boolean>>({});
  const [editedAnswers, setEditedAnswers] = useState<Record<string, any>>({});
  const [isSaving, setIsSaving] = useState<Record<string, boolean>>({});
  const [dealDocuments, setDealDocuments] = useState<Document[]>([]);

  // Filter states for each persona
  const [answerFilters, setAnswerFilters] = useState<Record<string, 'all' | 'answered' | 'not_answered'>>({});
  const [confidenceFilters, setConfidenceFilters] = useState<Record<string, 'all' | 'high' | 'medium' | 'low'>>({});
  const [sourceFilters, setSourceFilters] = useState<Record<string, string>>({});

  // Persona report generation state
  const [isGeneratingPersonaReport, setIsGeneratingPersonaReport] = useState<Record<string, boolean>>({});
  const [personaReports, setPersonaReports] = useState<Record<string, any>>({});

  // Persona report dialog state
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [selectedPersonaReport, setSelectedPersonaReport] = useState<{
    personaType: string;
    personaName: string;
    reportContent: any;
    reportId?: string;
  } | null>(null);

  // Check if user is authorized to see control buttons
  const isAuthorized = isAuthenticated && user && (user.role === "ADMIN" || user.role === "ANALYST");

  // Load initial data and set up polling
  useEffect(() => {
    loadInitialData();

    // Fetch deal title for display
    const fetchDealTitle = async () => {
      try {
        const d = await dealService.getDealById(dealId);
        setDeal(d);
        setDealTitle(d.title || "");
      } catch (error) {
        setDeal(null);
        setDealTitle("");
      }
    };

    // helper to check membership
    const includesUser = (arr?: any[]) => Array.isArray(arr) && userId && arr.includes(userId);

    const isAssignedAnalyst =
      isAnalyst &&
      !!userId &&
      (
        deal?.assignedAnalystId === userId ||
        deal?.assigned_analyst_id === userId ||
        includesUser(deal?.assignedAnalysts) ||
        includesUser(deal?.analysts) ||
        includesUser(deal?.assigned_users) // fallback if your API uses this
      );

    const isAssignedReviewerOrViewer =
      !!userId &&
      (
        deal?.assignedReviewerId === userId ||
        deal?.assigned_reviewer_id === userId ||
        includesUser(deal?.assignedReviewers) ||
        includesUser(deal?.reviewers) ||
        includesUser(deal?.viewers)
      ) ||
      isReviewerRole ||
      isViewerRole;


    // Set up polling for real-time updates
    const interval = setInterval(() => {
      if (reviewData?.summary?.isRunning) {
        loadReviewData();
      }
    }, 5000); // Poll every 5 seconds when review is running

    return () => clearInterval(interval);
  }, [dealId, reviewData?.summary?.isRunning]);

  // Load initial data (personas and review data)
  const loadInitialData = async () => {
    try {
      // Load available personas and deal's selected personas
      await Promise.all([
        loadAvailablePersonas(),
        loadDealPersonas(),
        loadReviewData(),
        loadDocuments(),
        loadExistingPersonaReports(),
      ]);
    } catch (error) {
      console.error('Failed to load initial data:', error);
    }
  };

  // Load available personas
  const loadAvailablePersonas = async () => {
    try {
      const personas = await personaService.getPersonaDefinitions();
      setAvailablePersonas(personas);
    } catch (error) {
      console.error('Failed to load available personas:', error);
    }
  };

  // Load deal's selected personas
  const loadDealPersonas = async () => {
    try {
      const personas = await dealService.getDealPersonas(dealId);
      setDealPersonas(personas);
    } catch (error) {
      console.error('Failed to load deal personas:', error);
    }
  };

  // Load review data from API
  const loadReviewData = async () => {
    // Use the new backend filtering approach without persona-specific filters for initial load
    await loadReviewDataWithFilters();
  };

  // Load existing persona reports
  const loadExistingPersonaReports = async () => {
    try {
      const { reportService } = await import('../services/reportService');
      const existingReports = await reportService.getPersonaReportsByDeal(dealId);

      // Convert existing reports to the format we need
      const reportsMap: Record<string, any> = {};
      for (const report of existingReports) {
        if (report.personaType && report.status === 'COMPLETED') {
          // Parse the report content
          let content = null;
          if (report.reportContent) {
            try {
              content = JSON.parse(report.reportContent);
            } catch (error) {
              console.warn('Failed to parse report content:', error);
              content = { content: report.reportContent };
            }
          }
          // Store both ID and content for backend download support
          reportsMap[report.personaType] = {
            id: report.id,
            content: content
          };
        }
      }

      setPersonaReports(reportsMap);
    } catch (error) {
      console.error('Failed to load existing persona reports:', error);
      // Don't throw error, just log it as this is not critical
    }
  };

  // Load documents for the deal
  const loadDocuments = async () => {
    try {
      const response = await documentService.getDocuments(dealId, {
        limit: 100, // Get all documents
        status: 'UPLOADED' // Only show uploaded documents
      });
      setDealDocuments(response.documents);
    } catch (error: any) {
      console.error("Failed to load documents:", error);
    }
  };

  // Start AI review
  const handleStartReview = async () => {
    try {
      setIsStarting(true);

      // Check if deal has selected personas
      if (!dealPersonas || dealPersonas.length === 0) {
        toast({
          title: "No AI Personas Selected",
          description: "Please configure AI personas for this deal before starting a review. You can set default personas or configure custom ones.",
          variant: "destructive",
        });
        return;
      }

      const result = await aiReviewService.startReview(dealId);

      toast({
        title: "AI Review Started",
        description: result.message,
      });

      // Immediately refresh data
      await loadReviewData();
    } catch (error: any) {
      // Check for specific persona-related errors
      const errorMessage = error.message || '';
      const isPersonaError = errorMessage.includes('No personas selected') ||
        errorMessage.includes('personas are not available') ||
        errorMessage.includes('Please select AI personas') ||
        errorMessage.includes('check your persona settings') ||
        errorMessage.includes('initialize personas') ||
        errorMessage.includes('No personas selected for this deal');

      if (isPersonaError) {
        toast({
          title: "No AI Personas Selected",
          description: "Please configure AI personas for this deal before starting a review. You can set default personas or configure custom ones.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Failed to Start Review",
          description: errorMessage || "An unexpected error occurred while starting the AI review.",
          variant: "destructive",
        });
      }
    } finally {
      setIsStarting(false);
    }
  };

  // Reset AI review
  const handleResetReview = async () => {
    try {
      setIsLoading(true);

      const result = await aiReviewService.resetReview(dealId);

      toast({
        title: "AI Review Reset",
        description: result.message,
      });

      // Immediately refresh data
      await loadReviewData();
    } catch (error: any) {
      toast({
        title: "Failed to Reset Review",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Get persona icon component
  const getPersonaIcon = (type: string) => {
    const iconMap: { [key: string]: any } = {
      LEGAL: Scale,
      FINANCIAL: DollarSign,
      TECHNICAL: Cog,
      OPERATIONAL: Building,
      COMPLIANCE: Shield,
      STRATEGIC: Target,
      RISK: AlertTriangle,
    };
    return iconMap[type] || Bot;
  };

  // Get persona color
  const getPersonaColor = (type: string): string => {
    return aiReviewService.getPersonaColor(type);
  };

  // Get status badge color
  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "COMPLETED":
        return "bg-green-100 text-green-800";
      case "IN_PROGRESS":
        return "bg-blue-100 text-blue-800";
      case "PENDING":
        return "bg-gray-100 text-gray-800";
      case "FAILED":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Get status icon for AI review status
  const getAIStatusIcon = (status: string) => {
    switch (status) {
      case "COMPLETED":
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case "IN_PROGRESS":
        return <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />;
      case "PENDING":
        return <Clock className="w-4 h-4 text-gray-600" />;
      case "FAILED":
        return <AlertTriangle className="w-4 h-4 text-red-600" />;
      default:
        return <Eye className="w-4 h-4 text-gray-600" />;
    }
  };

  // Format time remaining
  const formatTimeRemaining = (seconds: number): string => {
    if (seconds <= 0) return "Completed";

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m remaining`;
    } else if (minutes > 0) {
      return `${minutes}m remaining`;
    } else {
      return `${seconds}s remaining`;
    }
  };

  const handleEdit = (field: string, currentValue: string) => {
    setEditingField(field);
    setEditValue(currentValue);
  };

  const handleSave = (field: string) => {
    setExtractedData((prev) =>
      prev.map((item) =>
        item.field === field
          ? { ...item, value: editValue, status: "verified" as const }
          : item
      )
    );
    setEditingField(null);
    setEditValue("");
    toast({
      title: "Field Updated",
      description: "The extracted data has been updated and verified.",
    });
  };

  const handleStatusChange = (
    field: string,
    verification_status: ExtractedData["verification_status"]
  ) => {
    setExtractedData((prev) =>
      prev.map((item) => (item.field === field ? { ...item, verification_status } : item))
    );
  };

  const handleApprovePersona = (personaId: string) => {
    setPersonas((prev) =>
      prev.map((persona) =>
        persona.id === personaId
          ? {
            ...persona,
            status: "completed" as const,
            reviewedAt: new Date().toISOString(),
          }
          : persona
      )
    );
    toast({
      title: "Review Approved",
      description: "Persona analysis has been approved and marked as complete.",
    });
  };

  const handleAddComment = (personaId: string) => {
    if (!newComment.trim()) return;

    const comment = {
      id: Date.now().toString(),
      user: "Current User",
      text: newComment,
      timestamp: new Date().toISOString(),
    };

    setPersonas((prev) =>
      prev.map((persona) =>
        persona.id === personaId
          ? { ...persona, comments: [...persona.comments, comment] }
          : persona
      )
    );
    setNewComment("");
    toast({
      title: "Comment Added",
      description: "Your comment has been added to the review.",
    });
  };

  // Handler functions for editing Q&A data
  const updateEditedAnswer = (
    personaType: string,
    chapterName: string,
    categoryName: string,
    questionIndex: number,
    answerIndex: number,
    field: 'answer' | 'source_document' | 'confidence',
    value: string | number
  ) => {
    const key = `${personaType}-${chapterName}-${categoryName}-${questionIndex}-${answerIndex}`;
    setEditedAnswers(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value
      }
    }));
  };

  const getEditedAnswerValue = (
    personaType: string,
    chapterName: string,
    categoryName: string,
    questionIndex: number,
    answerIndex: number,
    field: 'answer' | 'source_document' | 'confidence',
    originalValue: any
  ) => {
    const key = `${personaType}-${chapterName}-${categoryName}-${questionIndex}-${answerIndex}`;
    return editedAnswers[key]?.[field] ?? originalValue;
  };

  // Filter functions that trigger backend calls
  const initializeDefaultFilters = (personaType: string) => {
    setAnswerFilters(prev => ({ ...prev, [personaType]: 'answered' }));
    setConfidenceFilters(prev => ({ ...prev, [personaType]: 'all' }));
    setSourceFilters(prev => ({ ...prev, [personaType]: 'all' }));
  };

  const updateAnswerFilter = async (personaType: string, filter: 'all' | 'answered' | 'not_answered') => {
    setAnswerFilters(prev => ({ ...prev, [personaType]: filter }));
    // Pass the new filter value directly to avoid stale state
    await loadReviewDataWithFilters(personaType, {
      answerFilter: filter,
      confidenceFilter: confidenceFilters[personaType] || 'high',
      sourceFilter: sourceFilters[personaType] || 'all'
    });
  };

  const updateConfidenceFilter = async (personaType: string, filter: 'all' | 'high' | 'medium' | 'low') => {
    setConfidenceFilters(prev => ({ ...prev, [personaType]: filter }));
    // Pass the new filter value directly to avoid stale state
    await loadReviewDataWithFilters(personaType, {
      answerFilter: answerFilters[personaType] || 'answered',
      confidenceFilter: filter,
      sourceFilter: sourceFilters[personaType] || 'all'
    });
  };

  const updateSourceFilter = async (personaType: string, sourceDocument: string) => {
    setSourceFilters(prev => ({ ...prev, [personaType]: sourceDocument }));
    // Pass the new filter value directly to avoid stale state
    await loadReviewDataWithFilters(personaType, {
      answerFilter: answerFilters[personaType] || 'answered',
      confidenceFilter: confidenceFilters[personaType] || 'all',
      sourceFilter: sourceDocument
    });
  };

  // Load review data with filters applied on backend
  const loadReviewDataWithFilters = async (
    personaType?: string,
    directFilters?: {
      answerFilter: 'all' | 'answered' | 'not_answered';
      confidenceFilter: 'all' | 'high' | 'medium' | 'low';
      sourceFilter: string;
    }
  ) => {
    try {
      setIsLoading(true);

      // If personaType is provided, use filters for that specific persona
      // Otherwise, apply default filters for initial load
      let filters;
      if (personaType) {
        filters = {
          answerFilter: directFilters?.answerFilter || answerFilters[personaType] || 'answered',
          confidenceFilter: directFilters?.confidenceFilter || confidenceFilters[personaType] || 'high',
          sourceFilter: directFilters?.sourceFilter || sourceFilters[personaType] || 'all',
          personaType: personaType
        };
      } else {
        // For initial load, apply default filters (answered questions with all confidence)
        filters = {
          answerFilter: 'answered',
          confidenceFilter: 'all',
          sourceFilter: 'all'
        };
      }

      // Load results with filters applied on backend
      const data = await aiReviewService.getResults(dealId, filters);
      setReviewData(data);
      setLastUpdated(new Date());

      // Update extracted data from AI review results
      if (data.results && data.results.length > 0) {
        const allExtractedData = aiReviewService.getAllExtractedData(
          data.results
        );
        const formattedData = allExtractedData.map((data) => ({
          category: data.category,
          chapter: data.chapter,
          field: data.field,
          value: data.value,
          confidence: data.confidence,
          source_document: data.source_document,
          page_reference: data.page_reference,
          verification_status: data.verification_status,
          persona: data.persona,
          personaType: data.personaType,
          notes:
            data.verification_status === "FLAGGED"
              ? "Requires verification"
              : undefined,
        }));
        setExtractedData(formattedData);

        // Initialize default filters for each persona (only on initial load)
        if (!personaType) {
          data.results.forEach(result => {
            const pType = result.persona.type;
            if (!answerFilters[pType]) {
              initializeDefaultFilters(pType);
            }
          });
        }
      }
    } catch (error: any) {
      console.error("Failed to load review data:", error);
      if (error.message?.includes("Authentication required")) {
        setAuthError(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveAllChanges = async (personaType: string) => {
    try {
      // Set saving state
      setIsSaving(prev => ({ ...prev, [personaType]: true }));

      // Validate all edited answers
      const personaEdits = Object.entries(editedAnswers).filter(([key]) =>
        key.startsWith(personaType)
      );

      for (const [key, edits] of personaEdits) {
        if (edits.answer && !edits.answer.trim()) {
          toast({
            title: "Validation Error",
            description: "All answers must have content before saving.",
            variant: "destructive",
          });
          return;
        }
        if (edits.source_document && !edits.source_document.trim()) {
          toast({
            title: "Validation Error",
            description: "All answers must have a source document before saving.",
            variant: "destructive",
          });
          return;
        }
      }

      // Update the review data with all edited answers
      setReviewData((prev) => {
        if (!prev) return prev;

        const updatedResults = prev.results.map((result) => {
          if (result.persona.type === personaType) {
            const updatedResult = { ...result };
            if (updatedResult.analysis?.extracted_data) {
              // Apply all edits for this persona
              Object.entries(editedAnswers).forEach(([key, edits]) => {
                if (key.startsWith(personaType)) {
                  const [, chapterName, categoryName, questionIndex, answerIndex] = key.split('-');
                  const chapterData = updatedResult.analysis.extracted_data[chapterName];

                  if (chapterData) {
                    const categoryQuestions = chapterData[categoryName];
                    if (categoryQuestions) {
                      // Group questions to avoid duplicates
                      const groupedQuestions = categoryQuestions.reduce((acc, qa) => {
                        const questionKey = qa.question.trim().toLowerCase();
                        if (!acc[questionKey]) {
                          acc[questionKey] = {
                            question: qa.question,
                            answers: []
                          };
                        }

                        const existingAnswer = acc[questionKey].answers.find(
                          existing => existing.answer === qa.answer && existing.source_document === qa.source_document
                        );

                        if (!existingAnswer) {
                          acc[questionKey].answers.push({
                            answer: qa.answer,
                            source_document: qa.source_document,
                            page_reference: qa.page_reference,
                            confidence: qa.confidence,
                            verification_status: qa.verification_status
                          });
                        }

                        return acc;
                      }, {} as Record<string, { question: string; answers: Array<any> }>);

                      const questionGroups = Object.values(groupedQuestions);
                      const qIndex = parseInt(questionIndex);
                      const aIndex = parseInt(answerIndex);

                      if (questionGroups[qIndex] && questionGroups[qIndex].answers[aIndex]) {
                        questionGroups[qIndex].answers[aIndex] = {
                          ...questionGroups[qIndex].answers[aIndex],
                          answer: edits.answer || questionGroups[qIndex].answers[aIndex].answer,
                          source_document: edits.source_document || questionGroups[qIndex].answers[aIndex].source_document,
                          confidence: edits.confidence || questionGroups[qIndex].answers[aIndex].confidence,
                          verification_status: "VERIFIED" as const
                        };

                        // Update the original category questions array
                        const updatedCategoryQuestions = categoryQuestions.map((qa) => {
                          const questionKey = qa.question.trim().toLowerCase();
                          const matchingGroup = Object.entries(groupedQuestions).find(([key]) => key === questionKey);
                          if (matchingGroup) {
                            const [, group] = matchingGroup;
                            const answerMatch = group.answers.find(ans =>
                              ans.answer === qa.answer && ans.source_document === qa.source_document
                            );
                            if (answerMatch) {
                              return {
                                ...qa,
                                answer: answerMatch.answer,
                                source_document: answerMatch.source_document,
                                confidence: answerMatch.confidence,
                                verification_status: answerMatch.verification_status
                              };
                            }
                          }
                          return qa;
                        });

                        updatedResult.analysis.extracted_data[chapterName][categoryName] = updatedCategoryQuestions;
                      }
                    }
                  }
                }
              });
            }
          }
          return result;
        });

        return {
          ...prev,
          results: updatedResults,
        };
      });

      // Clear edited answers for this persona
      const updatedEditedAnswers = { ...editedAnswers };
      Object.keys(updatedEditedAnswers).forEach(key => {
        if (key.startsWith(personaType)) {
          delete updatedEditedAnswers[key];
        }
      });
      setEditedAnswers(updatedEditedAnswers);

      // Exit edit mode
      setIsEditModeEnabled(prev => ({ ...prev, [personaType]: false }));

      // Call API to save changes to backend
      try {
        const updates = Object.entries(editedAnswers)
          .filter(([key]) => key.startsWith(personaType))
          .map(([key, edits]) => {
            const [, chapterName, categoryName, questionIndex, answerIndex] = key.split('-');
            return {
              chapterName,
              categoryName,
              questionIndex: parseInt(questionIndex),
              answerIndex: parseInt(answerIndex),
              answer: edits.answer,
              source_document: edits.source_document,
              confidence: edits.confidence
            };
          })
          .filter(update => update.answer || update.source_document || update.confidence);

        if (updates.length > 0) {
          await aiReviewService.updatePersonaAnswers(dealId, personaType, updates);

          // Refetch the AI review data to show updated answers
          await loadReviewData();

          toast({
            title: "Changes Saved",
            description: "All answers have been successfully updated and refreshed.",
          });
        } else {
          toast({
            title: "Changes Saved",
            description: "All answers have been successfully updated and verified.",
          });
        }
      } catch (apiError) {
        console.error('Failed to save to backend:', apiError);
        toast({
          title: "Backend Save Warning",
          description: "Changes saved locally but may not be persisted. Please try again.",
          variant: "destructive",
        });
        return; // Don't proceed with clearing edit state if API call failed
      }

    } catch (error) {
      toast({
        title: "Save Failed",
        description: "Failed to save changes. Please try again.",
        variant: "destructive",
      });
    } finally {
      // Clear saving state
      setIsSaving(prev => ({ ...prev, [personaType]: false }));
    }
  };

  const handleCancelAllChanges = (personaType: string) => {
    // Check if there are unsaved changes
    const hasChanges = Object.keys(editedAnswers).some(key => key.startsWith(personaType));

    if (hasChanges) {
      const confirmCancel = window.confirm("You have unsaved changes. Are you sure you want to cancel?");
      if (!confirmCancel) return;
    }

    // Clear all edits for this persona
    const updatedEditedAnswers = { ...editedAnswers };
    Object.keys(updatedEditedAnswers).forEach(key => {
      if (key.startsWith(personaType)) {
        delete updatedEditedAnswers[key];
      }
    });
    setEditedAnswers(updatedEditedAnswers);

    // Exit edit mode
    setIsEditModeEnabled(prev => ({ ...prev, [personaType]: false }));
  };

  const toggleEditMode = (personaType: string) => {
    setIsEditModeEnabled((prev) => ({
      ...prev,
      [personaType]: !prev[personaType],
    }));
  };

  // Handle persona report generation
  const handleGeneratePersonaReport = async (personaType: string, personaName: string) => {
    try {
      setIsGeneratingPersonaReport(prev => ({ ...prev, [personaType]: true }));

      // Import the report service
      const { reportService } = await import('../services/reportService');

      const report = await reportService.generatePersonaReport(dealId, {
        personaType: personaType as any,
      });

      // Store the generated report with ID and content
      setPersonaReports(prev => ({
        ...prev,
        [personaType]: {
          id: report.id,
          content: report.content
        }
      }));

      // Open the dialog with the new report
      setSelectedPersonaReport({
        personaType,
        personaName,
        reportContent: report.content,
        reportId: report.id
      });
      setIsReportDialogOpen(true);

      toast({
        title: "Report Generated Successfully",
        description: `${personaType.replace(/_/g, ' ').toLowerCase()} report has been generated and is ready to view.`,
      });

    } catch (error) {
      console.error('Failed to generate persona report:', error);
      toast({
        title: "Report Generation Failed",
        description: error instanceof Error ? error.message : "Failed to generate persona report",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingPersonaReport(prev => ({ ...prev, [personaType]: false }));
    }
  };

  // Handle viewing existing persona report
  const handleViewPersonaReport = (personaType: string, personaName: string) => {
    const reportData = personaReports[personaType];
    if (reportData) {
      setSelectedPersonaReport({
        personaType,
        personaName,
        reportContent: reportData.content,
        reportId: reportData.id
      });
      setIsReportDialogOpen(true);
    }
  };

  // Handle downloading report - now uses backend service like combined reports
  const handleDownloadReport = async (personaType: string, reportId?: string) => {
    if (!reportId) {
      toast({
        title: "Download Failed",
        description: "Report ID not found. Please regenerate the report.",
        variant: "destructive",
      });
      return;
    }

    try {
      toast({
        title: "Generating PDF",
        description: "Please wait while we generate your PDF report...",
      });

      // Import the report service
      const { reportService } = await import('../services/reportService');

      // Use the same backend download service as combined reports
      const pdfBlob = await reportService.downloadReport(reportId);

      // Create download link
      const url = window.URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `${personaType.toLowerCase().replace(/_/g, '-')}-report-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast({
        title: "Report Downloaded",
        description: `${personaType.replace(/_/g, ' ').toLowerCase()} report has been downloaded as PDF.`,
      });
    } catch (error) {
      console.error('Failed to download report:', error);
      toast({
        title: "Download Failed",
        description: error instanceof Error ? error.message : "Failed to download the PDF report.",
        variant: "destructive",
      });
    }
  };

  const getStatusColor = (status: ExtractedData["verification_status"]) => {
    switch (status) {
      case "VERIFIED":
        return "bg-green-100 text-green-800";
      case "FLAGGED":
        return "bg-red-100 text-red-800";
      case "PENDING":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getConfidenceColor = (confidence: number | string) => {
    if (confidence === 'N/A' || confidence === null) return "text-gray-600";
    if (typeof confidence === 'string') return "text-gray-600";
    if (confidence >= 90) return "text-green-600";
    if (confidence >= 70) return "text-yellow-600";
    return "text-red-600";
  };

  const categorizeData = () => {
    return extractedData.reduce((acc, item) => {
      if (!acc[item.category]) {
        acc[item.category] = [];
      }
      acc[item.category].push(item);
      return acc;
    }, {} as Record<string, ExtractedData[]>);
  };

  const getRiskAnalysis = () => {
    const flaggedItems = extractedData.filter(
      (item) => item.verification_status === "FLAGGED"
    );
    const lowConfidenceItems = extractedData.filter(
      (item) => typeof item.confidence === 'number' && item.confidence < 80
    );

    return {
      totalRisks: flaggedItems.length,
      lowConfidenceItems: lowConfidenceItems.length,
      overallRiskLevel:
        flaggedItems.length > 2
          ? "High"
          : flaggedItems.length > 0
            ? "Medium"
            : "Low",
    };
  };

  const riskAnalysis = getRiskAnalysis();
  const categorizedData = categorizeData();

  const getPersonaProgress = () => {
    const completed = personas.filter((p) => p.status === "completed").length;
    return (completed / personas.length) * 100;
  };

  const getPersonaStatusIcon = (status: PersonaAnalysis["status"]) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case "reviewing":
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case "pending":
        return <Eye className="w-4 h-4 text-gray-600" />;
    }
  };

  const getPersonaStatusColor = (status: PersonaAnalysis["status"]) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800";
      case "reviewing":
        return "bg-yellow-100 text-yellow-800";
      case "pending":
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-background to-gray-100/50 dark:from-gray-900 dark:via-background dark:to-gray-800/50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="space-y-4">
          {/* Back Navigation */}
          <div className="flex items-center">
            {(isAdmin || isAssignedAnalyst) ? (
              // Admin or assigned analyst -> Back to Document Upload
              <Link to={`/document-upload?dealId=${dealId}`}>
                <Button variant="ghost" size="sm" className="flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">Back to Document Upload</span>
                  <span className="sm:hidden">Back to Upload</span>
                </Button>
              </Link>
            ) : isAssignedReviewerOrViewer ? (

              <Link to={`/deals/${dealId}`}>
                <Button variant="ghost" size="sm" className="flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Back to Deals
                </Button>
              </Link>
            ) : (

              <Link to={previousStepNav.previousStepUrl}>
                <Button variant="ghost" size="sm" className="flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  {previousStepNav.previousStepText}
                </Button>
              </Link>
            )}
          </div>

          {/* Title and Controls */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl sm:text-3xl font-light text-foreground flex items-center gap-2 sm:gap-3">
                <Bot className="w-6 h-6 sm:w-8 sm:h-8 text-primary flex-shrink-0" />
                <span className="truncate">Multi-Perspective AI Review</span>
              </h1>
              <p className="text-muted-foreground font-light text-sm sm:text-base mt-1">
                {dealTitle ? dealTitle : "Deal"}
                {lastUpdated && (
                  <span className="block sm:inline sm:ml-4 text-xs">
                    Last updated: {lastUpdated.toLocaleTimeString()}
                  </span>
                )}
              </p>
            </div>

            {/* AI Review Controls */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-2 lg:gap-4">
              {isAuthorized && !reviewData?.summary?.isRunning && (
                <Button
                  onClick={handleStartReview}
                  disabled={isStarting || authLoading}
                  className="flex items-center justify-center gap-2 w-full sm:w-auto"
                >
                  {isStarting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                  {isStarting ? "Starting..." : "Start AI Review"}
                </Button>
              )}

              {isAuthorized && reviewData?.results && (
                <Button
                  variant="outline"
                  onClick={handleResetReview}
                  disabled={isLoading || authLoading}
                  className="flex items-center justify-center gap-2 text-orange-600 border-orange-200 hover:bg-orange-50 w-full sm:w-auto"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  <span className="hidden sm:inline">Reset Review</span>
                  <span className="sm:hidden">Reset</span>
                </Button>
              )}

              <Button
                variant="outline"
                size="sm"
                onClick={loadReviewData}
                disabled={isLoading || authLoading}
                className="flex items-center justify-center gap-2 w-full sm:w-auto"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Refresh
              </Button>
            </div>
          </div>
        </div>

        {/* Authentication Loading */}
        {authLoading && (
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Loader2 className="w-5 h-5 text-yellow-600 animate-spin" />
                <div>
                  <h3 className="font-medium text-yellow-800">Loading User Data</h3>
                  <p className="text-sm text-yellow-700 mt-1">
                    Please wait while we verify your permissions...
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Authentication Warning */}
        {authError && (
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
                <div>
                  <h3 className="font-medium text-yellow-800">
                    Authentication Required
                  </h3>
                  <p className="text-sm text-yellow-700 mt-1">
                    {authError} Some features are available without
                    authentication.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Persona Overview */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl font-light">
              AI Persona Analysis
            </CardTitle>
            <CardDescription className="font-light">
              {reviewData?.summary?.isRunning
                ? "AI analysts are currently processing documents in real-time"
                : "Multi-perspective review by specialized AI analysts"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Real-time AI Review Status */}
            {reviewData?.results && reviewData.results.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {reviewData.results.map((result) => {
                  const personaType = result.persona.type;
                  const Icon = getPersonaIcon(personaType);
                  const color = getPersonaColor(personaType);

                  return (
                    <Card
                      key={personaType}
                      className="cursor-pointer hover:shadow-md transition-shadow"
                    >
                      <CardContent className="p-4 h-full flex flex-col justify-between">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Icon className={`w-5 h-5 ${color}`} />
                            <span className="font-medium text-sm">
                              {aiReviewService.formatStatus(personaType)}
                            </span>
                          </div>
                          {getAIStatusIcon(result.status)}
                        </div>
                        <div className="space-y-2">
                          <Badge
                            className={getStatusBadgeColor(result.status)}
                            variant="secondary"
                          >
                            {aiReviewService.formatStatus(result.status)}
                          </Badge>

                          {/* Progress Bar */}
                          <div className="space-y-1">
                            <Progress value={result.progress} className="h-2" />
                            <div className="text-xs text-muted-foreground">
                              {result.documentsAnalyzed} of{" "}
                              {result.totalDocuments} docs
                            </div>
                          </div>

                          {/* Current Document */}
                          {result.currentDocument &&
                            result.status === "IN_PROGRESS" && (
                              <div className="text-xs text-blue-600 font-medium">
                                Processing document...
                              </div>
                            )}



                          {/* Completion Time */}
                          {result.status === "COMPLETED" &&
                            result.completedAt && (
                              <div className="text-xs text-green-600 font-medium">
                                Completed{" "}
                                {new Date(
                                  result.completedAt
                                ).toLocaleTimeString()}
                              </div>
                            )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : dealPersonas.length > 0 ? (
              /* Show deal personas when no review results available */
              <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {dealPersonas.map((personaType) => {
                  const Icon = getPersonaIcon(personaType);
                  const color = getPersonaColor(personaType);
                  const persona = availablePersonas.find(p => p.type === personaType);

                  return (
                    <Card
                      key={personaType}
                      className="cursor-pointer hover:shadow-md transition-shadow"
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Icon className={`w-5 h-5 ${color}`} />
                            <span className="font-medium text-sm">
                              {persona ? persona.name.split(' ')[0] : personaType}
                            </span>
                          </div>
                          <Eye className="w-4 h-4 text-gray-600" />
                        </div>
                        <div className="space-y-2">
                          <Badge
                            className="bg-gray-100 text-gray-800"
                            variant="secondary"
                          >
                            Ready
                          </Badge>

                          {/* Progress Bar */}
                          <div className="space-y-1">
                            <Progress value={0} className="h-2" />
                            <div className="text-xs text-muted-foreground">
                              Ready to analyze
                            </div>
                          </div>

                          <div className="text-xs text-muted-foreground">
                            {persona ? persona.description : `${personaType} analysis perspective`}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              /* No personas selected for deal */
              <div className="text-center py-8">
                <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
                <h3 className="text-lg font-medium mb-2 text-amber-800">
                  No AI Personas Selected
                </h3>
                <p className="text-muted-foreground mb-4">
                  This deal doesn't have any AI personas configured. Please select personas to enable AI analysis.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Main Content - Tabbed AI Persona Analysis */}
        <div className="space-y-6">
          {/* Real AI Review Results with Tabs */}
          {reviewData?.results && reviewData.results.length > 0 ? (
            <Tabs
              defaultValue={reviewData.results[0]?.persona.type}
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-3 lg:grid-cols-5 xl:grid-cols-8 gap-1 h-auto p-1">
                {reviewData.results.map((result) => {
                  const Icon = getPersonaIcon(result.persona.type);
                  const color = getPersonaColor(result.persona.type);

                  return (
                    <TabsTrigger
                      key={result.persona.type}
                      value={result.persona.type}
                      className="flex flex-col items-center gap-1 p-2 h-auto text-xs"
                    >
                      <Icon className={`w-4 h-4 ${color}`} />
                      <span className="truncate max-w-[80px]">
                        {result.persona.name.split(" ")[0]}
                      </span>
                      {getAIStatusIcon(result.status)}
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {reviewData.results.map((result) => {
                const Icon = getPersonaIcon(result.persona.type);
                const color = getPersonaColor(result.persona.type);

                // Check if this result has meaningful content
                const hasContent =
                  result.analysis &&
                  ((result.analysis.summary &&
                    result.analysis.summary !== "Analysis in progress...") ||
                    (result.analysis.risks &&
                      result.analysis.risks.length > 0) ||
                    (result.analysis.recommendations &&
                      result.analysis.recommendations.length > 0) ||
                    (result.analysis.extracted_data &&
                      Object.values(result.analysis.extracted_data).some(chapterData =>
                        Object.values(chapterData).some(categoryQuestions =>
                          categoryQuestions.length > 0
                        )
                      )));

                return (
                  <TabsContent
                    key={result.persona.type}
                    value={result.persona.type}
                  >
                    <Card>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Icon className={`w-6 h-6 ${color}`} />
                            <div>
                              <CardTitle className="text-lg font-light">
                                {result.persona.name}
                              </CardTitle>
                              <CardDescription className="font-light">
                                {result.status === "COMPLETED"
                                  ? hasContent
                                    ? `Completed ${result.completedAt
                                      ? new Date(
                                        result.completedAt
                                      ).toLocaleString()
                                      : "recently"
                                    }`
                                    : "Completed - No compatible documents found"
                                  : result.status === "IN_PROGRESS"
                                    ? "Analysis in progress..."
                                    : result.status === "REQUIRES_ATTENTION"
                                      ? "Requires attention - Check document formats"
                                      : "Pending analysis"}
                              </CardDescription>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Edit Mode Toggle Button */}
                            {result.status === "COMPLETED" && hasContent && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => toggleEditMode(result.persona.type)}
                                className="flex items-center gap-1"
                              >
                                <Edit3 className="w-3 h-3" />
                                {isEditModeEnabled[result.persona.type] ? "Exit Edit" : "Edit"}
                              </Button>
                            )}
                            {/* Persona Report Actions */}
                            {result.status === "COMPLETED" && hasContent && (
                              <>
                                {!personaReports[result.persona.type] ? (
                                  /* Generate Report Button - only show if no report exists */
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleGeneratePersonaReport(result.persona.type, result.persona.name)}
                                    className="flex items-center gap-1"
                                    disabled={isGeneratingPersonaReport[result.persona.type]}
                                  >
                                    {isGeneratingPersonaReport[result.persona.type] ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <FileText className="w-3 h-3" />
                                    )}
                                    {isGeneratingPersonaReport[result.persona.type] ? "Generating..." : "Generate Report"}
                                  </Button>
                                ) : (
                                  /* View Report Button - only show if report exists */
                                  <Button
                                    size="sm"
                                    onClick={() => handleViewPersonaReport(result.persona.type, result.persona.name)}
                                    className="flex items-center gap-1"
                                  >
                                    <Eye className="w-3 h-3" />
                                    View Report
                                  </Button>
                                )}
                              </>
                            )}
                            <Badge
                              className={getStatusBadgeColor(result.status)}
                              variant="secondary"
                            >
                              {aiReviewService.formatStatus(result.status)}
                            </Badge>
                            {getAIStatusIcon(result.status)}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {/* Progress Bar */}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-medium">Progress</h4>
                              <span className="text-sm font-medium">
                                {result.progress}%
                              </span>
                            </div>
                            <Progress value={result.progress} className="h-2" />
                          </div>

                          {/* Show message if no content available */}
                          {!hasContent && result.status === "COMPLETED" && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                              <div className="flex items-start gap-3">
                                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                                <div>
                                  <h4 className="font-medium text-amber-800 mb-1">
                                    No Analysis Available
                                  </h4>
                                  <p className="text-sm text-amber-700">
                                    This persona couldn't analyze any documents.
                                    The uploaded documents may not be in a
                                    format supported by AI analysis.
                                  </p>
                                  <p className="text-xs text-amber-600 mt-2">
                                    <strong>Supported formats:</strong> PDF,
                                    TXT, JPG, PNG
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Show message if requires attention */}
                          {result.status === "REQUIRES_ATTENTION" && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                              <div className="flex items-start gap-3">
                                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                                <div>
                                  <h4 className="font-medium text-red-800 mb-1">
                                    Analysis Failed
                                  </h4>
                                  <p className="text-sm text-red-700">
                                    This persona encountered issues during
                                    analysis. Please check that your documents
                                    are in supported formats and try again.
                                  </p>
                                  <p className="text-xs text-red-600 mt-2">
                                    <strong>Supported formats:</strong> PDF,
                                    TXT, JPG, PNG
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Analysis Results (only show if completed) */}
                          {result.status === "COMPLETED" && result.analysis && (
                            <>
                              {/* Confidence Score */}
                              {result.analysis.confidence_score && (
                                <div className="bg-card border rounded-lg p-4 mb-6">
                                  <div className="flex items-center justify-between mb-3">
                                    <h4 className="font-semibold">
                                      Analysis Confidence
                                    </h4>
                                    <span
                                      className={`text-lg font-bold ${result.analysis.confidence_score >= 80
                                        ? "text-green-600 dark:text-green-400"
                                        : result.analysis
                                          .confidence_score >= 60
                                          ? "text-yellow-600 dark:text-yellow-400"
                                          : "text-red-600 dark:text-red-400"
                                        }`}
                                    >
                                      {result.analysis.confidence_score}%
                                    </span>
                                  </div>
                                  <Progress
                                    value={result.analysis.confidence_score}
                                    className="h-3"
                                  />
                                  <p className="text-xs text-muted-foreground mt-2">
                                    Based on document quality and data
                                    completeness
                                  </p>
                                </div>
                              )}

                              {/* Sub-tabs for organized content */}
                              <Tabs defaultValue="extracted-data" className="w-full">
                                <TabsList className="grid w-full grid-cols-3 mb-6">
                                  <TabsTrigger value="extracted-data" className="text-sm">
                                    Key Data Extracted
                                  </TabsTrigger>
                                  <TabsTrigger value="risk-assessment" className="text-sm">
                                    Risk Assessment
                                  </TabsTrigger>
                                  <TabsTrigger value="recommendations" className="text-sm">
                                    Strategic Recommendations
                                  </TabsTrigger>
                                </TabsList>

                                {/* Risk Assessment Tab */}
                                <TabsContent value="risk-assessment" className="space-y-6">
                                  {result.analysis.risks &&
                                    result.analysis.risks.length > 0 ? (
                                    <div className="bg-card border rounded-lg p-4">
                                      <h4 className="font-semibold mb-4 flex items-center gap-2">
                                        <Shield className="w-5 h-5 text-red-500" />
                                        Risk Assessment
                                      </h4>
                                      <div className="space-y-3">
                                        {result.analysis.risks.map(
                                          (risk, index) => (
                                            <div
                                              key={index}
                                              className={`border-l-4 pl-4 py-3 ${risk.severity === "CRITICAL"
                                                ? "border-red-500 bg-red-50 dark:bg-red-950/20"
                                                : risk.severity === "HIGH"
                                                  ? "border-red-400 bg-red-50/50 dark:bg-red-950/10"
                                                  : risk.severity === "MEDIUM"
                                                    ? "border-yellow-400 bg-yellow-50/50 dark:bg-yellow-950/10"
                                                    : "border-blue-400 bg-blue-50/50 dark:bg-blue-950/10"
                                                }`}
                                            >
                                              <div className="flex items-start justify-between mb-2">
                                                <h5 className="font-medium">
                                                  {risk.title}
                                                </h5>
                                                <Badge
                                                  variant={
                                                    risk.severity === "CRITICAL"
                                                      ? "destructive"
                                                      : "outline"
                                                  }
                                                  className="text-xs"
                                                >
                                                  {risk.severity}
                                                </Badge>
                                              </div>
                                              <p className="text-sm text-muted-foreground mb-2 leading-relaxed">
                                                {risk.description}
                                              </p>
                                              {risk.category && (
                                                <p className="text-xs text-muted-foreground mb-2">
                                                  <strong>Category:</strong>{" "}
                                                  {risk.category}
                                                </p>
                                              )}
                                              {risk.recommendation && (
                                                <div className="bg-blue-50 dark:bg-blue-950/20 p-3 rounded border-l-2 border-blue-200 dark:border-blue-800">
                                                  <p className="text-sm text-blue-800 dark:text-blue-200">
                                                    <strong>Mitigation:</strong>{" "}
                                                    {risk.recommendation}
                                                  </p>
                                                </div>
                                              )}
                                              {risk.source_documents &&
                                                risk.source_documents.length >
                                                0 && (
                                                  <p className="text-xs text-muted-foreground mt-2">
                                                    <strong>Source:</strong>{" "}
                                                    {risk.source_documents.join(
                                                      ", "
                                                    )}
                                                  </p>
                                                )}
                                            </div>
                                          )
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="bg-muted/50 p-8 rounded-lg text-center">
                                      <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                                      <p className="text-muted-foreground">
                                        No risks identified in the analysis
                                      </p>
                                    </div>
                                  )}
                                </TabsContent>

                                {/* Strategic Recommendations Tab */}
                                <TabsContent value="recommendations" className="space-y-6">
                                  {result.analysis.recommendations &&
                                    result.analysis.recommendations.length > 0 ? (
                                    <div className="bg-card border rounded-lg p-4">
                                      <h4 className="font-semibold mb-4 flex items-center gap-2">
                                        <Target className="w-5 h-5 text-green-500" />
                                        Strategic Recommendations
                                      </h4>
                                      <div className="space-y-3">
                                        {result.analysis.recommendations.map(
                                          (rec, index) => (
                                            <div
                                              key={index}
                                              className={`border-l-4 pl-4 py-3 ${rec.priority === "HIGH"
                                                ? "border-green-500 bg-green-50 dark:bg-green-950/20"
                                                : rec.priority === "MEDIUM"
                                                  ? "border-yellow-400 bg-yellow-50/50 dark:bg-yellow-950/10"
                                                  : "border-blue-400 bg-blue-50/50 dark:bg-blue-950/10"
                                                }`}
                                            >
                                              <div className="flex items-start justify-between mb-2">
                                                <h5 className="font-medium">
                                                  {rec.title}
                                                </h5>
                                                <div className="flex items-center gap-2">
                                                  <Badge
                                                    variant={
                                                      rec.priority === "HIGH"
                                                        ? "default"
                                                        : "outline"
                                                    }
                                                    className="text-xs"
                                                  >
                                                    {rec.priority} Priority
                                                  </Badge>
                                                  {/* {rec.action_required && (
                                                    <Badge
                                                      variant="destructive"
                                                      className="text-xs"
                                                    >
                                                      Action Required
                                                    </Badge>
                                                  )} */}
                                                </div>
                                              </div>
                                              <p className="text-sm text-muted-foreground leading-relaxed">
                                                {rec.description}
                                              </p>
                                              {rec.source_documents &&
                                                rec.source_documents.length >
                                                0 && (
                                                  <p className="text-xs text-muted-foreground mt-2">
                                                    <strong>Source:</strong>{" "}
                                                    {rec.source_documents.join(
                                                      ", "
                                                    )}
                                                  </p>
                                                )}
                                            </div>
                                          )
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="bg-muted/50 p-8 rounded-lg text-center">
                                      <Target className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                                      <p className="text-muted-foreground">
                                        No strategic recommendations generated
                                      </p>
                                    </div>
                                  )}
                                </TabsContent>

                                {/* Key Data Extracted Tab */}
                                <TabsContent value="extracted-data" className="space-y-6">
                                  <div className="bg-card border rounded-lg p-4">
                                    <h4 className="font-semibold mb-4 flex items-center gap-2">
                                      Key Data Extracted
                                    </h4>

                                    {/* Filters Section - Always visible */}
                                    <div className="p-4 bg-muted/30 rounded-lg border mb-4">
                                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        {/* Answer Status Filter */}
                                        <div>
                                          <label className="text-xs font-medium text-muted-foreground mb-1 block">
                                            Question Status
                                          </label>
                                          <Select
                                            value={answerFilters[result.persona.type] || 'answered'}
                                            onValueChange={(value) => updateAnswerFilter(result.persona.type, value as 'all' | 'answered' | 'not_answered')}
                                          >
                                            <SelectTrigger className="text-xs">
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="all">All Questions</SelectItem>
                                              <SelectItem value="answered">Answered Only</SelectItem>
                                              <SelectItem value="not_answered">Not Answered</SelectItem>
                                            </SelectContent>
                                          </Select>
                                        </div>

                                        {/* Confidence Score Filter */}
                                        <div>
                                          <label className="text-xs font-medium text-muted-foreground mb-1 block">
                                            Confidence Score
                                          </label>
                                          <Select
                                            value={confidenceFilters[result.persona.type] || 'all'}
                                            onValueChange={(value) => updateConfidenceFilter(result.persona.type, value as 'all' | 'high' | 'medium' | 'low')}
                                          >
                                            <SelectTrigger className="text-xs">
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="all">All Scores</SelectItem>
                                              <SelectItem value="high">High (80%+)</SelectItem>
                                              <SelectItem value="medium">Medium (50-79%)</SelectItem>
                                              <SelectItem value="low">Low (&lt;50%)</SelectItem>
                                            </SelectContent>
                                          </Select>
                                        </div>

                                        {/* Source Document Filter */}
                                        <div>
                                          <label className="text-xs font-medium text-muted-foreground mb-1 block">
                                            Source Document
                                          </label>
                                          <Select
                                            value={sourceFilters[result.persona.type] || 'all'}
                                            onValueChange={(value) => updateSourceFilter(result.persona.type, value)}
                                          >
                                            <SelectTrigger className="text-xs">
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="all">All Documents</SelectItem>
                                              {dealDocuments.map((doc) => (
                                                <SelectItem key={doc.id} value={doc.originalName}>
                                                  <div className="flex flex-col">
                                                    <span className="text-xs">{doc.originalName}</span>
                                                    <span className="text-xs text-muted-foreground">
                                                      {(doc.fileSize / 1024).toFixed(1)} KB
                                                    </span>
                                                  </div>
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Data Content - Always show if analysis exists */}
                                    {result.analysis && result.analysis.extracted_data && (
                                      <Accordion type="multiple" className="w-full">
                                        {Object.entries(result.analysis.extracted_data)
                                          .sort(([a], [b]) => {
                                            // Extract chapter numbers for sorting (e.g., "Chapter 1", "1. Chapter Name", etc.)
                                            const getChapterNumber = (name: string) => {
                                              const match = name.match(/(\d+)/);
                                              return match ? parseInt(match[1]) : 999;
                                            };
                                            return getChapterNumber(a) - getChapterNumber(b);
                                          })
                                          .map(([chapterName, chapterData]) => (
                                            <AccordionItem key={chapterName} value={chapterName}>
                                              <AccordionTrigger className="text-left">
                                                <div className="flex items-center justify-between w-full mr-4">
                                                  <span className="font-medium">{chapterName}</span>
                                                  <Badge variant="outline" className="text-xs">
                                                    {Object.values(chapterData).reduce((total, categoryQuestions) => {
                                                      // Count unique questions instead of total Q&A pairs
                                                      const uniqueQuestions = categoryQuestions.reduce((acc, qa) => {
                                                        const questionKey = qa.question.trim().toLowerCase();
                                                        acc.add(questionKey);
                                                        return acc;
                                                      }, new Set());
                                                      return total + uniqueQuestions.size;
                                                    }, 0)} questions
                                                  </Badge>
                                                </div>
                                              </AccordionTrigger>
                                              <AccordionContent>
                                                <div className="space-y-4 pt-2">
                                                  {Object.entries(chapterData).map(([categoryName, questions]) => {
                                                    // Group questions by question text to avoid duplicates
                                                    const groupedQuestions = questions.reduce((acc, qa) => {
                                                      const questionKey = qa.question.trim().toLowerCase();
                                                      if (!acc[questionKey]) {
                                                        acc[questionKey] = {
                                                          question: qa.question,
                                                          answers: []
                                                        };
                                                      }

                                                      // Only add unique answers (avoid duplicates from same source)
                                                      const existingAnswer = acc[questionKey].answers.find(
                                                        existing => existing.answer === qa.answer && existing.source_document === qa.source_document
                                                      );

                                                      if (!existingAnswer) {
                                                        acc[questionKey].answers.push({
                                                          answer: qa.answer,
                                                          source_document: qa.source_document,
                                                          page_reference: qa.page_reference,
                                                          confidence: qa.confidence,
                                                          verification_status: qa.verification_status
                                                        });
                                                      }

                                                      return acc;
                                                    }, {} as Record<string, { question: string; answers: Array<any> }>);

                                                    return (
                                                      <div key={categoryName} className="border-l-2 border-blue-200 dark:border-blue-800 pl-4">
                                                        <h6 className="font-medium text-sm text-blue-700 dark:text-blue-300 mb-3">
                                                          {categoryName}
                                                        </h6>
                                                        <div className="grid grid-cols-1 gap-4">
                                                          {Object.values(groupedQuestions).length > 0 ? (
                                                            Object.values(groupedQuestions).map((questionGroup, questionIndex) => {
                                                              // Calculate overall confidence and verification status
                                                              // Filter out N/A confidence values for average calculation
                                                              const validAnswers = questionGroup.answers.filter(ans =>
                                                                ans.confidence !== 'N/A' && ans.confidence !== null && typeof ans.confidence === 'number'
                                                              );

                                                              const avgConfidence = validAnswers.length > 0
                                                                ? Math.round(validAnswers.reduce((sum, ans) => sum + ans.confidence, 0) / validAnswers.length)
                                                                : 'N/A';

                                                              const hasVerified = questionGroup.answers.some(ans => ans.verification_status === "VERIFIED");
                                                              const hasFlagged = questionGroup.answers.some(ans => ans.verification_status === "FLAGGED");

                                                              return (
                                                                <div
                                                                  key={questionIndex}
                                                                  className={`p-4 rounded-lg border ${hasVerified || hasFlagged
                                                                    ? "bg-green-50/50 dark:bg-green-950/10 border-green-200 dark:border-green-800"  : "bg-muted/50 border-border"   }`}
                                                                >
                                                                  {/* Question Header */}
                                                                  <div className="flex items-start justify-between mb-3">
                                                                    <h5 className="font-medium text-sm leading-relaxed">
                                                                      {questionGroup.question}
                                                                    </h5>
                                                                    <div className="flex items-center gap-1 ml-2">
                                                                      <Badge
                                                                        variant={avgConfidence === 'N/A' ? "secondary" : (avgConfidence >= 80 ? "default" : "outline")}
                                                                        className="text-xs"
                                                                      >
                                                                        {avgConfidence === 'N/A' ? 'N/A' : `${avgConfidence}%`} avg
                                                                      </Badge>
                                                                      {/* {hasVerified && (
                                                                        <CheckCircle className="w-3 h-3 text-green-600" />
                                                                      )}
                                                                      {hasFlagged && (
                                                                        <AlertTriangle className="w-3 h-3 text-red-600" />
                                                                      )} */}
                                                                      <Badge variant="secondary" className="text-xs">
                                                                        {questionGroup.answers.length} source{questionGroup.answers.length !== 1 ? 's' : ''}
                                                                      </Badge>
                                                                    </div>
                                                                  </div>

                                                                  {/* Multiple Answers */}
                                                                  <div className="space-y-3">
                                                                    {questionGroup.answers.map((answer, answerIndex) => (
                                                                      <div
                                                                        key={answerIndex}
                                                                        className="pl-4 border-l-2 border-blue-100 dark:border-blue-900"
                                                                      >
                                                                        {isEditModeEnabled[result.persona.type] ? (
                                                                          /* Edit Mode - All answers editable */
                                                                          <div className="space-y-3 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                                                                            <div>
                                                                              <label className="text-xs font-medium text-muted-foreground mb-1 block">
                                                                                Answer
                                                                              </label>
                                                                              <Textarea
                                                                                value={getEditedAnswerValue(
                                                                                  result.persona.type,
                                                                                  chapterName,
                                                                                  categoryName,
                                                                                  questionIndex,
                                                                                  answerIndex,
                                                                                  'answer',
                                                                                  answer.answer
                                                                                )}
                                                                                onChange={(e) => updateEditedAnswer(
                                                                                  result.persona.type,
                                                                                  chapterName,
                                                                                  categoryName,
                                                                                  questionIndex,
                                                                                  answerIndex,
                                                                                  'answer',
                                                                                  e.target.value
                                                                                )}
                                                                                className="text-sm"
                                                                                rows={3}
                                                                              />
                                                                            </div>
                                                                            <div className="grid grid-cols-2 gap-3">
                                                                              <div>
                                                                                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                                                                                  Source Document
                                                                                </label>
                                                                                <Select
                                                                                  value={getEditedAnswerValue(
                                                                                    result.persona.type,
                                                                                    chapterName,
                                                                                    categoryName,
                                                                                    questionIndex,
                                                                                    answerIndex,
                                                                                    'source_document',
                                                                                    answer.source_document
                                                                                  )}
                                                                                  onValueChange={(value) => updateEditedAnswer(
                                                                                    result.persona.type,
                                                                                    chapterName,
                                                                                    categoryName,
                                                                                    questionIndex,
                                                                                    answerIndex,
                                                                                    'source_document',
                                                                                    value
                                                                                  )}
                                                                                >
                                                                                  <SelectTrigger className="text-xs">
                                                                                    <SelectValue placeholder="Select document" />
                                                                                  </SelectTrigger>
                                                                                  <SelectContent>
                                                                                    {dealDocuments.map((doc) => (
                                                                                      <SelectItem key={doc.id} value={doc.originalName}>
                                                                                        <div className="flex flex-col">
                                                                                          <span className="text-xs">{doc.originalName}</span>
                                                                                          <span className="text-xs text-muted-foreground">
                                                                                            {(doc.fileSize / 1024).toFixed(1)} KB
                                                                                          </span>
                                                                                        </div>
                                                                                      </SelectItem>
                                                                                    ))}
                                                                                  </SelectContent>
                                                                                </Select>
                                                                              </div>
                                                                              <div>
                                                                                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                                                                                  Confidence Score (%)
                                                                                </label>
                                                                                <div className="flex gap-2 items-center">
                                                                                  <Input
                                                                                    type="text"
                                                                                    placeholder="0-100 or N/A"
                                                                                    value={getEditedAnswerValue(
                                                                                      result.persona.type,
                                                                                      chapterName,
                                                                                      categoryName,
                                                                                      questionIndex,
                                                                                      answerIndex,
                                                                                      'confidence',
                                                                                      answer.confidence
                                                                                    )}
                                                                                    onChange={(e) => {
                                                                                      const value = e.target.value.trim();
                                                                                      let parsedValue;

                                                                                      if (value.toLowerCase() === 'n/a' || value === '') {
                                                                                        parsedValue = 'N/A';
                                                                                      } else {
                                                                                        const numValue = parseInt(value);
                                                                                        if (!isNaN(numValue)) {
                                                                                          parsedValue = Math.max(0, Math.min(100, numValue));
                                                                                        } else {
                                                                                          parsedValue = 'N/A';
                                                                                        }
                                                                                      }

                                                                                      updateEditedAnswer(
                                                                                        result.persona.type,
                                                                                        chapterName,
                                                                                        categoryName,
                                                                                        questionIndex,
                                                                                        answerIndex,
                                                                                        'confidence',
                                                                                        parsedValue
                                                                                      );
                                                                                    }}
                                                                                    className="text-xs flex-1"
                                                                                  />
                                                                                </div>
                                                                              </div>
                                                                            </div>
                                                                          </div>
                                                                        ) : (
                                                                          /* Display Mode */
                                                                          <div>
                                                                            <div className="flex items-start justify-between mb-1">
                                                                              <p className="text-sm font-normal text-foreground">
                                                                                {answer.answer}
                                                                              </p>
                                                                              <div className="flex items-center gap-1 ml-2">
                                                                                <Badge
                                                                                  variant={answer.confidence === 'N/A' ? "secondary" : (answer.confidence >= 80 ? "default" : "outline")}
                                                                                  className="text-xs"
                                                                                >
                                                                                  {answer.confidence === 'N/A' ? 'N/A' : `${answer.confidence}%`}
                                                                                </Badge>
                                                                                {/* {answer.verification_status === "VERIFIED" && (
                                                                                  <CheckCircle className="w-3 h-3 text-green-600" />
                                                                                )}
                                                                                {answer.verification_status === "FLAGGED" && (
                                                                                  <AlertTriangle className="w-3 h-3 text-red-600" />
                                                                                )} */}
                                                                              </div>
                                                                            </div>
                                                                            <div className="text-xs text-muted-foreground">
                                                                              <span>
                                                                                <strong>Source:</strong> {answer.source_document}
                                                                              </span>
                                                                            </div>
                                                                          </div>
                                                                        )}
                                                                      </div>
                                                                    ))}
                                                                  </div>
                                                                </div>
                                                              );
                                                            })
                                                          ) : (
                                                            <div className="text-center py-4 text-muted-foreground">
                                                              <p className="text-sm">
                                                                {answerFilters[result.persona.type] === 'not_answered'
                                                                  ? 'No unanswered questions in this category'
                                                                  : answerFilters[result.persona.type] === 'answered'
                                                                    ? 'No questions answered for this category'
                                                                    : 'No questions found for this category'
                                                                }
                                                              </p>
                                                            </div>
                                                          )}
                                                        </div>
                                                      </div>
                                                    );
                                                  })}
                                                </div>
                                              </AccordionContent>
                                            </AccordionItem>
                                          ))}
                                      </Accordion>
                                    )}
                                  </div>
                                </TabsContent>
                              </Tabs>

                              {/* Save All Changes Section */}
                              {isEditModeEnabled[result.persona.type] && (
                                <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                                  <div className="flex items-center justify-between">
                                    <div>

                                      <p className="text-sm text-blue-600 dark:text-blue-300">
                                        {isSaving[result.persona.type]
                                          ? "Updating answers and refreshing data..."
                                          : "All answers are now editable. Make your changes and save when ready."
                                        }
                                      </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleCancelAllChanges(result.persona.type)}
                                        className="flex items-center gap-1"
                                        disabled={isSaving[result.persona.type]}
                                      >
                                        <X className="w-4 h-4" />
                                        Cancel
                                      </Button>
                                      <Button
                                        size="sm"
                                        onClick={() => handleSaveAllChanges(result.persona.type)}
                                        className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700"
                                        disabled={isSaving[result.persona.type]}
                                      >
                                        {isSaving[result.persona.type] ? (
                                          <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                          <Save className="w-4 h-4" />
                                        )}
                                        {isSaving[result.persona.type] ? "Saving..." : "Save All Changes"}
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </>
                          )}

                          {/* In Progress State */}
                          {result.status === "IN_PROGRESS" && (
                            <div className="text-center py-4">
                              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-blue-600" />
                              <p className="text-sm text-muted-foreground">
                                AI analysis in progress...
                              </p>
                            </div>
                          )}

                          {/* Pending State */}
                          {result.status === "PENDING" && (
                            <div className="text-center py-4">
                              <Clock className="w-6 h-6 mx-auto mb-2 text-gray-400" />
                              <p className="text-sm text-muted-foreground">
                                Waiting to start analysis...
                              </p>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                );
              })}
            </Tabs>
          ) : (
            /* No Results State */
            <Card>
              <CardContent className="text-center py-8">
                <Bot className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <h3 className="text-lg font-medium mb-2">
                  No AI Analysis Available
                </h3>
                <p className="text-muted-foreground mb-4">
                  Start an AI review to see detailed analysis from our
                  specialized AI personas.
                </p>
                {!reviewData?.summary?.isRunning && (
                  <Button onClick={handleStartReview} disabled={isStarting}>
                    {isStarting ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Play className="w-4 h-4 mr-2" />
                    )}
                    Start AI Review
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Action Buttons */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-center">
              <div className="text-sm text-muted-foreground">
                {reviewData?.results && reviewData.results.length > 0
                  ? `${reviewData.results.filter((r) => r.status === "COMPLETED")
                    .length
                  } of ${reviewData.results.length} analyses completed`
                  : dealPersonas.length > 0
                    ? `${dealPersonas.length} personas configured for this deal`
                    : "No personas configured for this deal"}
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                {(isAdmin || isAssignedAnalyst) && (
                  // Admin or assigned analyst -> Back to Document Upload
                  <Link to={`/document-upload?dealId=${dealId}`} className="w-full sm:w-auto">
                    <Button variant="outline" className="flex items-center justify-center gap-2 w-full sm:w-auto">
                      <ArrowLeft className="w-4 h-4" />
                      <span className="hidden sm:inline">Back to Document Upload</span>
                      <span className="sm:hidden">Back to Upload</span>
                    </Button>
                  </Link>
                )}

                {isAssignedReviewerOrViewer && (
                  // Assigned reviewer or viewer -> Back to Deals (deal details)
                  <Link to={`/deals/${dealId}`} className="w-full sm:w-auto">
                    <Button variant="outline" className="flex items-center justify-center gap-2 w-full sm:w-auto">
                      <ArrowLeft className="w-4 h-4" />
                      Back to Deals
                    </Button>
                  </Link>
                )}

                {/* Report generation stays visible for all */}
                <Link to={`/report-generation?dealId=${dealId}`} className="w-full sm:w-auto">
                  <Button
                    className="flex items-center justify-center gap-2 w-full sm:w-auto"
                    disabled={
                      !reviewData?.results ||
                      reviewData.results.filter((r) => r.status === "COMPLETED").length < reviewData.results.length
                    }
                  >
                    Generate Report
                    <FileText className="w-4 h-4" />
                  </Button>
                </Link>
              </div>



            </div>
          </CardContent>
        </Card>
      </div>

      {/* Persona Report Dialog */}
      <Dialog open={isReportDialogOpen} onOpenChange={setIsReportDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              {selectedPersonaReport?.personaName} Report
            </DialogTitle>
            <DialogDescription>
              Detailed analysis report for {selectedPersonaReport?.personaType?.replace(/_/g, ' ').toLowerCase()} persona
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-hidden">
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 max-h-[60vh] overflow-y-auto border border-gray-200 dark:border-gray-600">
              {selectedPersonaReport?.reportContent ? (
                <div className="prose prose-sm max-w-none dark:prose-invert">
                  <ReactMarkdown
                    components={{
                      h1: ({ children }) => <h1 className="text-xl font-bold mt-6 mb-4 text-foreground border-b border-border pb-2">{children}</h1>,
                      h2: ({ children }) => <h2 className="text-lg font-semibold mt-5 mb-3 text-foreground">{children}</h2>,
                      h3: ({ children }) => <h3 className="text-base font-medium mt-4 mb-2 text-foreground">{children}</h3>,
                      p: ({ children }) => <p className="mb-3 text-foreground leading-relaxed">{children}</p>,
                      ul: ({ children }) => <ul className="list-disc list-inside mb-3 text-foreground">{children}</ul>,
                      ol: ({ children }) => <ol className="list-decimal list-inside mb-3 text-foreground">{children}</ol>,
                      li: ({ children }) => <li className="mb-1 text-foreground">{children}</li>,
                      strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
                      em: ({ children }) => <em className="italic text-muted-foreground">{children}</em>,
                      code: ({ children }) => <code className="bg-muted px-1 py-0.5 rounded text-xs font-mono text-foreground">{children}</code>,
                      pre: ({ children }) => <pre className="bg-muted p-3 rounded text-xs font-mono overflow-x-auto text-foreground">{children}</pre>,
                    }}
                  >
                    {extractReportContent(selectedPersonaReport.reportContent)}
                  </ReactMarkdown>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Loading report content...</p>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="flex-shrink-0">
            <div className="flex items-center gap-2 w-full">
              <Button
                variant="outline"
                onClick={() => {
                  if (selectedPersonaReport) {
                    handleDownloadReport(selectedPersonaReport.personaType, selectedPersonaReport.reportId);
                  }
                }}
                className="flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download Report
              </Button>

              <Button
                variant="outline"
                onClick={() => {
                  if (selectedPersonaReport) {
                    setIsReportDialogOpen(false);
                    // Wait for dialog to close before generating new report
                    setTimeout(() => {
                      handleGeneratePersonaReport(selectedPersonaReport.personaType, selectedPersonaReport.personaName);
                    }, 300);
                  }
                }}
                disabled={selectedPersonaReport ? isGeneratingPersonaReport[selectedPersonaReport.personaType] : false}
                className="flex items-center gap-2"
              >
                {selectedPersonaReport && isGeneratingPersonaReport[selectedPersonaReport.personaType] ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Regenerate Report
              </Button>

              <div className="flex-1" />

              <Button
                variant="secondary"
                onClick={() => setIsReportDialogOpen(false)}
              >
                Close
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AIReview;
